import { Alert, Button, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { LaneMetricRow, LanePolylineReport as Report } from '@/apis/simulation';
import { EMPTY, fmtCount, fmtPp, fmtRatio } from '../format';

const categories: Record<string, string> = {
  lane_center_line: '中心线', lane_boundary: '车道边界', road_boundary: '路沿', stop_line: '停止线',
};
const reasonLabels: Record<string, string> = {
  lane_execution_incomplete: '评分帧不完整，未纳入基准',
  lane_payload_not_object: '尚未收到有效的 Lane 评分报告',
  lane_runtime_incomplete: '运行未正常结束（超时或异常退出），未纳入基准',
  lane_runtime_not_completed: '运行尚未结束，未纳入基准',
  lane_runtime_unavailable: '运行记录不可用，未纳入基准',
  lane_job_fact_row_budget_exceeded: '整次汇总的预测行数超过处理限制',
  lane_job_fact_byte_budget_exceeded: '整次汇总的文件总大小超过处理限制',
  lane_report_disk_budget_exceeded: '汇总所需的临时存储超过限制',
  lane_report_time_budget_exceeded: '汇总耗时超过处理限制',
  lane_fact_storage_unavailable: '汇总临时存储不可用',
  lane_fact_file_unavailable: '预测明细文件暂时无法读取',
  lane_ap_input_reference_invalid: '精简评分产物未生成或引用无效，无法计算整批 AP',
  lane_ap_input_checksum_invalid: '精简评分产物完整性校验失败',
  lane_ap_input_file_unavailable: '精简评分文件暂时无法读取',
  lane_ap_input_record_invalid: '精简评分记录格式不正确',
  lane_ap_input_order_invalid: '精简评分记录未按置信度排序',
  lane_ap_input_counts_mismatch: '精简评分记录与 checker 上报计数不一致',
  lane_ap_memory_budget_exceeded: '精简评分汇总超过内存处理限制',
};
const reasonLabel = (reason: string) => Object.prototype.hasOwnProperty.call(reasonLabels, reason) ? reasonLabels[reason] : reason;
const rowKey = (row: LaneMetricRow) => `${row.category}:${row.threshold_m}`;
const columns: ColumnsType<LaneMetricRow> = [
  { title: '线类别', dataIndex: 'category', render: (v: string) => categories[v] || v },
  { title: '距离阈值（米）', dataIndex: 'threshold_m' },
  ...(['gt', 'predictions', 'tp', 'fp', 'fn'] as const).map((key) => ({
    title: ({ gt: 'GT线数', predictions: '预测线数', tp: '匹配正确', fp: '多检', fn: '漏检' })[key],
    dataIndex: key, render: fmtCount,
  })),
  ...(['precision', 'recall', 'ap'] as const).map((key) => ({
    title: ({ precision: 'Precision', recall: 'Recall', ap: 'AP' })[key], dataIndex: key, render: (value: number | null) => fmtRatio(value),
  })),
];

export default function LanePolylineReport({ report, onViewLog }: {
  report: Report; onViewLog: (taskId: number) => void;
}) {
  const summary = report.summary;
  return <>
    <div className="pe-report-card">
      <div className="pe-report-card-title">Lane · 四类折线评测</div>
      <p>提交 {summary.submitted} 条 · 收到报告 {summary.with_metrics} 条 · 正常结束且评分完整 {summary.complete_scenes} 条</p>
      <Tag>业务达标线未配置</Tag>
      <p>计数单位是折线实例，阈值单位是米。Precision / Recall 从累计计数重算，AP 从逐条预测及置信度重算。这里只评四类线，不包含拓扑质量。</p>
      <p>已完成场景可先查看评分，汇总范围随结果更新。“—”表示指标不可用，不是 0 分或通过。</p>
    </div>
    {summary.groups.length === 0 && <Alert type="warning" showIcon message="没有可用的 Lane 评分" description="请查看下方场景错误；不会用默认分数代替缺失结果。" />}
    {summary.groups.map((group) => <div className="pe-report-card" key={group.profile_signature}>
      <div className="pe-report-card-title">同口径汇总 · {group.contributing_scenes} 条完整贡献场景</div>
      <Tag color={group.complete ? 'blue' : 'orange'}>{group.complete ? '全场景完整' : '部分结果'} · 覆盖 {group.contributing_scenes}/{summary.submitted} 场景</Tag>
      {!group.complete && <p>以下分数仅来自已完成的同口径场景；尚无完整评分的场景未计入。</p>}
      <p>{group.complete ? 'mAP' : '已完成场景 mAP'} {fmtRatio(group.map)} · 纳入 {group.map_contributing_category_thresholds} 组类别/阈值（无 GT 的类别不计入）</p>
      {group.profile.profile_id === 'native_lane_polyline_roi_v1' && <p>
        评测对象：感知模块发布的四类车道线。范围以车顶雷达坐标为准：
        {JSON.stringify(group.profile.roi_bounds_m)} 米（xmin、ymin、zmin、xmax、ymax、zmax）；
        按 XY 裁切，排除原标注忽略区，每条线保留最长连续段且长度大于 2 米。
      </p>}
      <code>{group.profile_signature}</code>
      <Table<LaneMetricRow> rowKey={rowKey} size="small" pagination={false} columns={columns} dataSource={group.rows} scroll={{ x: 1050 }} />
      <details><summary>本次实际评测配置</summary><pre>{JSON.stringify(group.profile, null, 2)}</pre></details>
      {group.fact_errors.length > 0 && <Alert type="warning" message="预测明细或汇总不可用，无法重算 Job AP" description={group.fact_errors.map(e => `${e.scene_id === null ? '整次汇总' : `场景 ${e.scene_id}`}: ${e.reasons.map(reasonLabel).join('，')}`).join('；')} />}
    </div>)}
    {summary.excluded.length > 0 && <div className="pe-report-card">
      <div className="pe-report-card-title">未纳入完整汇总的场景</div>
      {summary.excluded.map((e, i) => <p key={i}>场景 {e.scene_id}：{e.reasons.map(reasonLabel).join('，')}</p>)}
    </div>}
    {report.compare && <div className="pe-report-card">
      <div className="pe-report-card-title">基线比较 · 共同兼容场景 {report.compare.common_source_scenes.length} 条</div>
      <p>只比较相同源场景、GT版本、评分窗与规则签名；模型版本可以不同。排除 {report.compare.excluded.length} 条。</p>
      {!report.compare.available ? <Alert type="warning" message="没有共同兼容的场景，差异不可计算" /> :
        <Table rowKey={r => `${r.profile_signature}:${r.category}:${r.threshold_m}`} size="small" pagination={false} dataSource={report.compare.deltas} columns={[
          { title: '规则签名', dataIndex: 'profile_signature', ellipsis: true },
          { title: '类别', dataIndex: 'category', render: (v: string) => categories[v] || v },
          { title: '阈值（米）', dataIndex: 'threshold_m' },
          ...(['precision_pp', 'recall_pp', 'ap_pp'] as const).map(k => ({ title: `${k}（百分点）`, dataIndex: k, render: (value: number | null) => fmtPp(value) })),
        ]} />}
      <details><summary>对比范围及排除原因</summary><pre>{JSON.stringify(report.compare.excluded, null, 2)}</pre></details>
    </div>}
    <div className="pe-report-card">
      <div className="pe-report-card-title">逐场景 · 点击展开看来源和错误</div>
      <Table rowKey="scene_id" size="small" dataSource={report.cases} columns={[
        { title: '源场景', render: (_, r) => r.payload?.source_scene_id || `平台场景 ${r.scene_id}` },
        { title: '评测执行', render: (_, r) => r.payload_validation_errors?.length ? '报告格式错误' : r.runtime_error ? reasonLabel(r.runtime_error) : r.payload?.execution_state === 'completed' ? '执行完成' : r.payload?.execution_state || '没有报告' },
        { title: '业务达标', render: () => '未配置' },
        { title: 'GT / 评分 / 缺预测帧', render: (_, r) => `${fmtCount(r.payload?.frames?.eligible_gt)} / ${fmtCount(r.payload?.frames?.scored)} / ${fmtCount(r.payload?.frames?.missing_prediction)}` },
        { title: '错误', render: (_, r) => [...(r.payload_validation_errors || []), ...(r.payload?.errors || []), ...(r.fact_error ? [r.fact_error] : []), ...(r.runtime_error ? [r.runtime_error] : [])].map(reasonLabel).join('，') || EMPTY },
        { title: '查看', render: (_, r) => <Button size="small" onClick={() => onViewLog(r.task_id)}>日志</Button> },
      ]} expandable={{ expandedRowRender: r => <>
        {r.payload?.rows && <Table rowKey={rowKey} size="small" pagination={false} columns={columns} dataSource={r.payload.rows} scroll={{ x: 1050 }} />}
        <pre>{JSON.stringify({ 来源: r.payload?.provenance, 配置: r.payload?.profile, 真值校验值: r.payload?.gt_metadata_sha256, 明细文件: r.payload?.facts, 帧统计: r.payload?.frames }, null, 2)}</pre>
      </> }} />
    </div>
  </>;
}
